/*******************************************************************
* PAGE        : ToggleSwitchComponent
* REMARKS     :
* CREATED ON  : 16-07-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 16-07-2026
********************************************************************/

import React, { useCallback, useId, useRef } from 'react';
import './ToggleSwitchComponent.css';

// The three allowed values, left to right.
const POSITIONS = ['no', 'default', 'yes'];

// #region Component
const ToggleSwitchComponent = ({
    value, onChange, label,
    noLabel = 'No', defaultLabel = 'Default', yesLabel = 'Yes',
    disabled = false,
    noColor, defaultColor, yesColor, thumbColor,
    size = 'md', className = '', style = {}, ...props
}) => {

    // #region States
    const generatedId = useId();
    const trackRef = useRef(null);

    const activeIndex = POSITIONS.indexOf(value) === -1 ? 1 : POSITIONS.indexOf(value);

    const commitValue = useCallback(
        (nextValue) => {
            if (disabled) return;
            if (nextValue === value) return;
            onChange && onChange(nextValue);
        },
        [disabled, onChange, value]
    );
    // #endregion

    // #region Methods
    const handleSegmentClick = (position) => {
        commitValue(position);
    };

    const handleKeyDown = (event) => {
        if (disabled) return;

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            const nextIndex = Math.max(activeIndex - 1, 0);
            commitValue(POSITIONS[nextIndex]);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            const nextIndex = Math.min(activeIndex + 1, POSITIONS.length - 1);
            commitValue(POSITIONS[nextIndex]);
        } else if (event.key === 'Home') {
            event.preventDefault();
            commitValue(POSITIONS[0]);
        } else if (event.key === 'End') {
            event.preventDefault();
            commitValue(POSITIONS[POSITIONS.length - 1]);
        }
    };

    const trackColorVars = {
        ...(noColor ? { '--toggle-no-color': noColor } : {}),
        ...(defaultColor ? { '--toggle-default-color': defaultColor } : {}),
        ...(yesColor ? { '--toggle-yes-color': yesColor } : {}),
        ...(thumbColor ? { '--toggle-thumb-color': thumbColor } : {}),
    };

    const wrapperClasses = [
        'toggle-switch-container',
        disabled ? 'disabled' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const trackClasses = [
        'toggle-switch-track',
        `size-${size}`,
        `pos-${POSITIONS[activeIndex]}`,
    ]
        .filter(Boolean)
        .join(' ');
    // #endregion

    return (
        <React.Fragment>
            <div className={wrapperClasses} style={style}>
                {label && (
                    <label className="toggle-switch-label" htmlFor={generatedId}>
                        {label}
                    </label>
                )}

                <div
                    id={generatedId}
                    ref={trackRef}
                    role="radiogroup"
                    aria-label={label || 'toggle switch'}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    className={trackClasses}
                    style={trackColorVars}
                    onKeyDown={handleKeyDown}
                    {...props}
                >
                    {/* Static hint letters — visible only while the thumb sits centered
                    in "default", one at each end of the track. */}
                    <span className="toggle-switch-end-label toggle-switch-end-label--left" aria-hidden="true">
                        {noLabel.charAt(0)}
                    </span>
                    <span className="toggle-switch-end-label toggle-switch-end-label--right" aria-hidden="true">
                        {yesLabel.charAt(0)}
                    </span>

                    {/* Visible text sits opposite the thumb, single-state-friendly like the reference design.
                    The center ("default") position stays clean/unlabeled since the thumb takes the middle. */}
                    <span className="toggle-switch-text" aria-hidden="true">
                        {POSITIONS[activeIndex] === 'no' && noLabel}
                        {POSITIONS[activeIndex] === 'yes' && yesLabel}
                    </span>

                    <div className="toggle-switch-thumb" aria-hidden="true" />

                    {/* Invisible full-width click targets for each position — kept for a11y (radio semantics)
                    and so clicking/tapping anywhere along the track jumps straight to that position. */}
                    {POSITIONS.map((position, index) => {
                        const text = position === 'no' ? noLabel : position === 'yes' ? yesLabel : defaultLabel;

                        return (
                            <button
                                key={position}
                                type="button"
                                role="radio"
                                aria-checked={activeIndex === index}
                                aria-label={text}
                                className={`toggle-switch-segment ${activeIndex === index ? 'active' : ''}`}
                                disabled={disabled}
                                onClick={() => handleSegmentClick(position)}
                                tabIndex={-1}
                            />
                        );
                    })}
                </div>
            </div>
        </React.Fragment>
    );
};
// #endregion

export default ToggleSwitchComponent;